import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, ExternalLink, Loader2, Check, X as XIcon } from "lucide-react";
import { useAuth } from "../../../../lib/auth";
import * as api from "../../../../lib/api/adapters/http";
import { isMockApi } from "../../../../lib/api";
import * as repository from "@/data/storefrontRepository";
import { slugify } from "../../../../lib/utils";
import { ORDER_MODES } from "@/domain/restaurantOrders";
import { resizeTables } from "@/domain/restaurantTables";
import { getOrders } from "@/data/orderRepository";
import { normalizeTaxSettings, validateTaxRate } from "@/domain/restaurantOrders";
import { TABLE_REQUEST_TYPES, normalizeRequestTypes } from "@/domain/tableServiceRequests";
import StoreQrCard from "./components/StoreQrCard";

function settingsSnapshot({ form, fields, orderMode, tableCount, taxSettings, requestTypes }) {
  return JSON.stringify({ form, fields, orderMode, tableCount, taxSettings, requestTypes });
}

export default function StoreSettingsPage() {
  const { slugAvailable, DEFAULT_CHECKOUT_FIELDS, DEFAULT_BOOKING_FIELDS } = repository;
  const { vendor, updateVendor, refresh } = useAuth();
  const isRestaurant = vendor.businessType === "restaurant";
  const [form, setForm] = useState({
    name: vendor.name,
    slug: vendor.slug,
    tagline: vendor.tagline,
    description: vendor.description,
    logo: vendor.logo,
    coverImage: vendor.coverImage,
    address: vendor.address,
    phone: vendor.phone,
  });
  const [fields, setFields] = useState(
    isRestaurant
      ? { ...DEFAULT_CHECKOUT_FIELDS, ...(vendor.checkoutFields || {}) }
      : { ...DEFAULT_BOOKING_FIELDS, ...(vendor.bookingFields || {}) }
  );
  const [busy, setBusy] = useState(false);
  const [orderMode, setOrderMode] = useState(vendor.orderMode || "counter");
  const [tableCount, setTableCount] = useState(String(vendor.tables?.length ?? 1));
  const [tableError, setTableError] = useState("");
  const [taxSettings, setTaxSettings] = useState(normalizeTaxSettings(vendor.storefronts?.[0]?.taxSettings));
  const [taxError, setTaxError] = useState("");
  const [requestTypes, setRequestTypes] = useState(normalizeRequestTypes(vendor.storefronts?.[0]?.serviceRequestTypes));
  const [requestTypeError, setRequestTypeError] = useState("");
  const [slugCheck, setSlugCheck] = useState({ state: "available", message: "Store URL is available." });
  const [savedSnapshot, setSavedSnapshot] = useState(() => settingsSnapshot({
    form: {
      name: vendor.name, slug: vendor.slug, tagline: vendor.tagline, description: vendor.description,
      logo: vendor.logo, coverImage: vendor.coverImage, address: vendor.address, phone: vendor.phone,
    },
    fields: isRestaurant
      ? { ...DEFAULT_CHECKOUT_FIELDS, ...(vendor.checkoutFields || {}) }
      : { ...DEFAULT_BOOKING_FIELDS, ...(vendor.bookingFields || {}) },
    orderMode: vendor.orderMode || "counter",
    tableCount: String(vendor.tables?.length ?? 1),
    taxSettings: normalizeTaxSettings(vendor.storefronts?.[0]?.taxSettings),
    requestTypes: normalizeRequestTypes(vendor.storefronts?.[0]?.serviceRequestTypes),
  }));
  const [savedTableSettings, setSavedTableSettings] = useState({ orderMode: vendor.orderMode || "counter", tableCount: String(vendor.tables?.length ?? 1) });
  const setField = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const currentSnapshot = settingsSnapshot({ form, fields, orderMode, tableCount, taxSettings, requestTypes });
  const hasChanges = currentSnapshot !== savedSnapshot;
  const requestedSlug = slugify(form.slug || form.name);
  const saveDisabled = busy || !hasChanges || slugCheck.state !== "available";
  const tableSettingsChanged = orderMode !== savedTableSettings.orderMode || tableCount !== savedTableSettings.tableCount;

  useEffect(() => {
    if (!requestedSlug) {
      setSlugCheck({ state: "invalid", message: "Enter a valid Store URL." });
      return undefined;
    }
    if (isMockApi) {
      const available = slugAvailable(requestedSlug, vendor.id);
      setSlugCheck({ state: available ? "available" : "unavailable", message: available ? "Store URL is available." : "That Store URL is already taken." });
      return undefined;
    }
    if (requestedSlug === vendor.slug) {
      setSlugCheck({ state: "available", message: "Store URL is available." });
      return undefined;
    }

    let cancelled = false;
    setSlugCheck({ state: "checking", message: "Checking Store URL…" });
    const timer = window.setTimeout(async () => {
      try {
        const result = await api.checkOwnStorefrontSlug(requestedSlug);
        if (!cancelled) setSlugCheck({ state: result.available ? "available" : "unavailable", message: result.available ? "Store URL is available." : "That Store URL is already taken." });
      } catch (error) {
        if (!cancelled) setSlugCheck({ state: "invalid", message: error.message || "Could not check Store URL." });
      }
    }, 350);
    return () => { cancelled = true; window.clearTimeout(timer); };
  }, [requestedSlug, slugAvailable, vendor.id, vendor.slug]);

  const storeUrl = `${window.location.origin}/store/${vendor.slug}`;

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(storeUrl);
      toast.success("Store link copied");
    } catch {
      toast.error("Couldn't copy");
    }
  };

  const save = async () => {
    if (!hasChanges) return;
    if (!form.name.trim()) return toast.error("Business name is required");
    const s = slugify(form.slug || form.name);
    if (!s) return toast.error("Store URL is required");
    if (slugCheck.state !== "available") return toast.error(slugCheck.message);
    if (isMockApi && !slugAvailable(s, vendor.id)) return toast.error("That store URL is taken");
    setBusy(true);
    const patch = { ...form, slug: s };
    let validatedTax;
    if (isRestaurant) patch.checkoutFields = fields;
    else patch.bookingFields = fields;
    if (isRestaurant) {
      if (!ORDER_MODES.includes(orderMode)) { setBusy(false); return toast.error("Choose a valid order mode"); }
      if (!vendor.capabilities?.tableOrdering && orderMode !== "counter") { setBusy(false); return toast.error("Enable table ordering before choosing Table or Both mode"); }
      patch.orderMode = orderMode;
      if (["table", "both"].includes(orderMode) && vendor.capabilities?.tableOrdering && tableSettingsChanged) {
        const count = Number(tableCount);
        if (!Number.isInteger(count) || count < 1) { setTableError("Enter a positive whole number of tables."); setBusy(false); return; }
        if (isMockApi) {
          const result = resizeTables({ storefrontId: vendor.storefronts?.[0]?.id, count, tables: vendor.tables || [], orders: getOrders(vendor.id) });
          if (!result.ok) { setTableError(result.error); setBusy(false); return; }
          if (result.removed?.length && !window.confirm(`Remove ${result.removed.map(({ label }) => label).join(", ")}?`)) { setBusy(false); return; }
          patch.tables = result.tables;
        }
      }
      validatedTax = validateTaxRate(taxSettings.rate);
      if (!validatedTax.ok) { setTaxError(validatedTax.error); setBusy(false); return; }
      const primaryId = vendor.storefronts?.[0]?.id;
      if (vendor.capabilities?.tableServiceRequests && requestTypes.length === 0) { setRequestTypeError("Enable at least one table request type."); setBusy(false); return; }
      patch.storefronts = vendor.storefronts.map((storefront) => storefront.id === primaryId ? { ...storefront, taxSettings: { ...taxSettings, rate: validatedTax.rate }, serviceRequestTypes: requestTypes } : storefront);
    }
    try {
      if (isMockApi) {
        updateVendor(patch);
      } else {
        const availability = await api.checkOwnStorefrontSlug(s);
        if (!availability.available) {
          setBusy(false);
          setSlugCheck({ state: "unavailable", message: "That Store URL is already taken." });
          toast.error("That store URL is taken");
          return;
        }

        const storefrontPatch = {
          name: form.name.trim(), slug: s, tagline: form.tagline,
          description: form.description, logoUrl: form.logo, coverUrl: form.coverImage,
          address: form.address, phone: form.phone,
        };
        if (isRestaurant) {
          storefrontPatch.checkoutFields = fields;
          storefrontPatch.taxSettings = { ...taxSettings, rate: validatedTax.rate };
          storefrontPatch.serviceRequestTypes = requestTypes;
        }
        await api.updateOwnStorefront(storefrontPatch);
        await api.updateOwnRestaurant({
          name: form.name.trim(), address: form.address || undefined, phone: form.phone || undefined,
          ...(isRestaurant ? { orderMode } : {}),
        });
        if (isRestaurant && ["table", "both"].includes(orderMode) && vendor.capabilities?.tableOrdering && tableSettingsChanged) {
          await api.resizeVendorTables({ storefrontId: vendor.storefronts?.[0]?.id, count: Number(tableCount) });
        }
        await refresh();
      }
      setSavedSnapshot(currentSnapshot);
      setSavedTableSettings({ orderMode, tableCount });
      setBusy(false);
      toast.success("Store settings saved");
    } catch (error) {
      setBusy(false);
      toast.error(error.message || "Unable to save store settings");
    }
  };

  const toggleField = (key, prop) => {
    setFields((f) => {
      const next = { ...f, [key]: { ...f[key], [prop]: !f[key][prop] } };
      // If disabling, also un-require
      if (prop === "enabled" && next[key].enabled === false) next[key].required = false;
      return next;
    });
  };
  const updateFieldLabel = (key, prop, value) => {
    setFields((f) => ({ ...f, [key]: { ...f[key], [prop]: value } }));
  };

  return (
    <div className="space-y-8">
      <div>
        <div className="text-xs uppercase tracking-widest text-ink-muted">Configuration</div>
        <h1 className="mt-1 text-3xl font-display font-medium tracking-tight" data-testid="settings-title">Store settings</h1>
        <p className="mt-1 text-ink-secondary">Public storefront details customers see.</p>
      </div>

      {/* Store link card */}
      <div className="card-surface p-5">
        <div className="text-xs uppercase tracking-widest text-ink-muted">Storefront link</div>
        <div className="mt-3 flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
          <div className="flex-1 min-w-0 rounded-lg bg-bg-elevated border border-line px-4 py-3 font-mono text-sm truncate" data-testid="store-url">
            {storeUrl}
          </div>
          <div className="flex gap-2">
            <button onClick={copyUrl} className="btn-ghost inline-flex items-center gap-2" data-testid="copy-url-btn">
              <Copy className="h-4 w-4" /> Copy
            </button>
            <a href={`/store/${vendor.slug}`} target="_blank" rel="noreferrer" className="btn-primary inline-flex items-center gap-2" data-testid="open-store-btn">
              <ExternalLink className="h-4 w-4" /> Open
            </a>
          </div>
        </div>
      </div>

      {/* Details */}
      <div className="card-surface p-5 space-y-5">
        <div className="grid gap-5 sm:grid-cols-2">
          <FormRow label="Business name" required>
            <input className="input-field" value={form.name} onChange={setField("name")} data-testid="settings-name-input" />
          </FormRow>
          <FormRow label="Store URL slug" required>
            <div className="flex items-stretch gap-2">
              <span className="px-3 grid place-items-center rounded-lg border border-line bg-bg-elevated text-sm text-ink-muted whitespace-nowrap">/store/</span>
              <input className={`input-field ${slugCheck.state === "available" ? "border-emerald-500 focus:border-emerald-500" : slugCheck.state === "checking" ? "border-brand" : "border-red-500 focus:border-red-500"}`} value={form.slug} onChange={(e) => setForm({ ...form, slug: slugify(e.target.value) })} data-testid="settings-slug-input" />
            </div>
            <p className={`mt-1 flex items-center gap-1 text-xs ${slugCheck.state === "available" ? "text-emerald-600" : slugCheck.state === "checking" ? "text-ink-muted" : "text-red-600"}`} role="status" data-testid="settings-slug-status">
              {slugCheck.state === "available" ? <Check className="h-3.5 w-3.5" /> : slugCheck.state === "checking" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <XIcon className="h-3.5 w-3.5" />}
              {slugCheck.message}
            </p>
          </FormRow>
          <FormRow label="Tagline" className="sm:col-span-2">
            <input className="input-field" value={form.tagline} onChange={setField("tagline")} data-testid="settings-tagline-input" />
          </FormRow>
          <FormRow label="Description" className="sm:col-span-2">
            <textarea className="input-field min-h-[100px] resize-y" value={form.description} onChange={setField("description")} data-testid="settings-description-input" />
          </FormRow>
          <FormRow label="Address">
            <input className="input-field" value={form.address} onChange={setField("address")} data-testid="settings-address-input" />
          </FormRow>
          <FormRow label="Phone">
            <input className="input-field" value={form.phone} onChange={setField("phone")} data-testid="settings-phone-input" />
          </FormRow>
          <FormRow label="Logo URL">
            <input className="input-field" value={form.logo} onChange={setField("logo")} data-testid="settings-logo-input" />
          </FormRow>
          <FormRow label="Cover image URL">
            <input className="input-field" value={form.coverImage} onChange={setField("coverImage")} data-testid="settings-cover-input" />
          </FormRow>
        </div>

        <div className="flex justify-end pt-4 border-t border-line">
          <button onClick={save} disabled={saveDisabled} className="btn-primary inline-flex items-center gap-2 disabled:opacity-60" data-testid="settings-save-btn">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save changes"}
          </button>
        </div>
      </div>

      {isRestaurant && (
        <div className="card-surface p-5 space-y-5" data-testid="restaurant-order-settings">
          <div><h2 className="font-display text-lg font-medium">Restaurant ordering &amp; tax</h2><p className="text-sm text-ink-secondary">Choose how staff and guests identify orders and how tax is calculated.</p></div>
          <fieldset><legend className="text-xs uppercase tracking-widest text-ink-muted mb-2">Order mode</legend><div className="grid sm:grid-cols-3 gap-2">{ORDER_MODES.map((mode) => { const disabled = mode !== "counter" && !vendor.capabilities?.tableOrdering; return <label key={mode} className={`min-h-[44px] rounded-lg border border-line px-3 flex items-center gap-2 ${disabled ? "opacity-50" : ""}`}><input type="radio" name="order-mode" disabled={disabled} checked={orderMode === mode} onChange={() => { setOrderMode(mode); setTableError(""); }} data-testid={`order-mode-${mode}`} /><span className="capitalize">{mode}</span></label>; })}</div>{!vendor.capabilities?.tableOrdering && <p className="mt-2 text-sm text-ink-secondary" role="status">Table and Both modes require the Platform Admin table-ordering capability.</p>}</fieldset>
          {vendor.capabilities?.tableOrdering && ["table", "both"].includes(orderMode) && <label className="block max-w-xs text-sm">Number of tables<input type="number" min="1" step="1" className="input-field mt-1" value={tableCount} onChange={(event) => { setTableCount(event.target.value); setTableError(""); }} data-testid="table-count-input" />{tableError && <span role="alert" className="block mt-1 text-red-400">{tableError}</span>}</label>}
          <section className="border-t border-line pt-5 space-y-4" data-testid="tax-settings"><div><h3 className="font-display text-lg">Tax</h3><p className="text-sm text-ink-secondary">Suggested product default: 5%. Confirm the applicable rate and display rules with your accountant.</p></div><label className="min-h-[44px] flex items-center gap-3"><input type="checkbox" checked={taxSettings.enabled} onChange={(event) => setTaxSettings({ ...taxSettings, enabled: event.target.checked })} data-testid="tax-enabled" /> Apply tax to new orders</label>{taxSettings.enabled && <><label className="block text-sm">Tax percentage<input type="number" min="0" max="100" step="0.01" className="input-field mt-1 max-w-xs" value={taxSettings.rate} onChange={(event) => { setTaxSettings({ ...taxSettings, rate: event.target.value }); setTaxError(""); }} data-testid="tax-rate" />{taxError && <span role="alert" className="block mt-1 text-red-400">{taxError}</span>}</label><label className="min-h-[44px] flex items-center gap-3"><input type="checkbox" checked={taxSettings.showTax} onChange={(event) => setTaxSettings({ ...taxSettings, showTax: event.target.checked })} data-testid="tax-show" /> Show tax as a separate line</label></>}</section>
          <div className="flex justify-stretch sm:justify-end pt-4 border-t border-line"><button onClick={save} disabled={saveDisabled} className="btn-primary w-full sm:w-auto min-h-[44px] inline-flex items-center justify-center gap-2 disabled:opacity-60" data-testid="save-ordering-tax-settings">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save changes"}</button></div>
          {vendor.capabilities?.tableServiceRequests && <section className="border-t border-line pt-5 space-y-3" data-testid="service-request-settings"><div><h3 className="font-display text-lg">Table Help requests</h3><p className="text-sm text-ink-secondary">Choose what guests may request after scanning a valid table QR.</p></div><div className="grid sm:grid-cols-3 gap-2">{TABLE_REQUEST_TYPES.map((type) => <label key={type.id} className="min-h-[44px] flex items-center gap-2 rounded-lg border border-line px-3"><input type="checkbox" checked={requestTypes.includes(type.id)} onChange={(event) => { setRequestTypes((current) => event.target.checked ? [...current, type.id] : current.filter((id) => id !== type.id)); setRequestTypeError(""); }} data-testid={`service-request-type-${type.id}`} />{type.label}</label>)}</div>{requestTypeError && <p role="alert" className="text-sm text-red-600">{requestTypeError}</p>}</section>}
          {!vendor.capabilities?.tableServiceRequests && vendor.capabilities?.tableOrdering && ["table", "both"].includes(orderMode) && <p className="border-t border-line pt-4 text-sm text-ink-secondary" data-testid="service-request-admin-guidance">Table Help requests are unavailable until Platform Admin enables Table service requests for this business.</p>}
        </div>
      )}

      <QrCard vendor={vendor} />

      {/* Customer fields config */}
      <div className="card-surface p-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="font-display text-lg font-medium">
              {isRestaurant ? "Checkout fields" : "Booking fields"}
            </div>
            <p className="mt-1 text-sm text-ink-secondary max-w-lg">
              Choose what details you ask customers for at {isRestaurant ? "checkout" : "booking"}.
              Toggle a field off to hide it entirely — or keep it on but optional.
            </p>
          </div>
        </div>

        <div className="mt-6 space-y-3">
          {Object.entries(fields).map(([key, f]) => (
            <div key={key} className="rounded-xl border border-line bg-bg-elevated p-4" data-testid={`field-config-${key}`}>
              <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-5">
                <div className="min-w-0 flex-1">
                  <input
                    className="w-full bg-transparent border-0 p-0 text-sm font-medium text-ink-primary focus:outline-none"
                    value={f.label}
                    onChange={(e) => updateFieldLabel(key, "label", e.target.value)}
                    data-testid={`field-label-${key}`}
                  />
                  <div className="mt-0.5 text-[11px] text-ink-muted font-mono uppercase tracking-widest">
                    {key}
                  </div>
                </div>
                <div className="flex items-center gap-4 flex-shrink-0">
                  <Toggle
                    label="Show"
                    checked={!!f.enabled}
                    onChange={() => toggleField(key, "enabled")}
                    testid={`field-enabled-${key}`}
                  />
                  <Toggle
                    label="Required"
                    checked={!!f.required}
                    onChange={() => toggleField(key, "required")}
                    disabled={!f.enabled}
                    testid={`field-required-${key}`}
                  />
                </div>
              </div>
              {f.enabled && (
                <input
                  className="input-field mt-3 !py-2 text-xs"
                  value={f.placeholder}
                  onChange={(e) => updateFieldLabel(key, "placeholder", e.target.value)}
                  placeholder="Placeholder shown in the input"
                  data-testid={`field-placeholder-${key}`}
                />
              )}
            </div>
          ))}
        </div>

        <div className="mt-6 rounded-lg bg-bg-elevated border border-line p-3 text-xs text-ink-muted">
          <span className="text-ink-secondary font-medium">Tip: </span>
          You control every field customers see. By default, only Name is required —
          Phone is optional, Address & Notes are hidden. Turn them on if you need them.
        </div>

        <div className="flex justify-end pt-4 mt-4 border-t border-line">
          <button onClick={save} disabled={saveDisabled} className="btn-primary inline-flex items-center gap-2 disabled:opacity-60" data-testid="settings-save-fields-btn">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

function QrCard({ vendor }) {
  return <StoreQrCard vendor={vendor} />;
}

function Toggle({ label, checked, onChange, disabled, testid }) {
  return (
    <label className={`flex items-center gap-2 text-xs uppercase tracking-widest cursor-pointer select-none ${disabled ? "opacity-40 cursor-not-allowed" : ""}`}>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={disabled ? undefined : onChange}
        disabled={disabled}
        data-testid={testid}
        className={`relative h-5 w-9 rounded-full transition-colors ${
          checked ? "bg-brand" : "bg-bg-surface border border-line"
        }`}
      >
        <span
          className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-transform ${
            checked ? "left-4" : "left-0.5"
          }`}
        />
      </button>
      <span className={checked ? "text-ink-primary" : "text-ink-muted"}>{label}</span>
    </label>
  );
}

function FormRow({ label, required, className = "", children }) {
  return (
    <div className={className}>
      <div className="text-xs uppercase tracking-widest text-ink-muted mb-2">
        {label}
        {required && <span className="text-restaurant ml-1">*</span>}
      </div>
      {children}
    </div>
  );
}
